import { css } from '@emotion/react';
import { useState } from 'react';

import { useToast } from '@/Client/State';
import { LAYOUT2, TYPOGRAPHY2 } from '@/Client/Styles';
import { Button } from '@/Client/Widgets';
import { ExportsData, ScanEditorShowTagsKebabAndMenu, ScanOverviewPageData } from '@kaiizen/kai-pro-api';
import { useOrgSettings } from '@kaiizen/kai-pro-api-react-state';

type SettingsData = ExportsData | ScanOverviewPageData | ScanEditorShowTagsKebabAndMenu;

type SettingValue = {
  value: boolean;
  type?: string;
  helpText?: string;
  helpTitleText?: string;
};

/**
 * Type assertion helper for settings values.
 * Note: The API schema defines `data` as `any`, but we know from the schema definitions
 * (ExportsData, ScanOverviewPageData, ScanEditorShowTagsKebabAndMenu) that all values
 * follow the SettingValue structure.
 */
const asSettingValue = (setting: unknown): SettingValue => setting as SettingValue;

const MODAL_OVERLAY_STYLE = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
`;

const MODAL_CONTENT_STYLE = css`
  background: var(--background);
  padding: ${LAYOUT2.spacing3}px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 10px 20px rgba(0, 0, 0, 0.15);
`;

const MODAL_TITLE_STYLE = css`
  ${TYPOGRAPHY2.largeBold}
  color: var(--text);
  text-align: center;
  margin-bottom: ${LAYOUT2.spacing3}px;
`;

const SETTINGS_SECTION_STYLE = css`
  background: var(--background2);
  padding: ${LAYOUT2.spacing2}px;
  margin-bottom: ${LAYOUT2.spacing3}px;
`;

const SECTION_TITLE_STYLE = css`
  ${TYPOGRAPHY2.mediumBold}
  color: var(--text);
  margin-bottom: ${LAYOUT2.spacing2}px;
  padding-bottom: ${LAYOUT2.spacing1}px;
  border-bottom: 1px solid var(--border);
`;

const SETTING_ROW_STYLE = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${LAYOUT2.spacing2}px 0;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const SETTING_INFO_STYLE = css`
  flex: 1;
  margin-right: ${LAYOUT2.spacing2}px;
`;

const SETTING_LABEL_STYLE = css`
  ${TYPOGRAPHY2.medium}
  color: var(--text);
  display: block;
  margin-bottom: ${LAYOUT2.spacing1}px;
`;

const SETTING_HELP_TEXT_STYLE = css`
  ${TYPOGRAPHY2.small}
  color: var(--text2);
`;

const TOGGLE_SWITCH_STYLE = css`
  position: relative;
  width: 44px;
  height: 24px;
  background: var(--border);
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }
`;

const TOGGLE_SWITCH_CHECKED_STYLE = css`
  background: var(--backgroundGreen3);
`;

const TOGGLE_SLIDER_STYLE = css`
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  transition: transform 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const TOGGLE_SLIDER_CHECKED_STYLE = css`
  transform: translateX(20px);
`;

const MODAL_BUTTONS_STYLE = css`
  display: flex;
  gap: ${LAYOUT2.spacing2}px;
  justify-content: flex-end;
`;

const MODAL_BUTTON_STYLE = css`
  min-width: 100px;
`;

const LOGO_UPLOAD_SECTION_STYLE = css`
  background: var(--background2);
  padding: ${LAYOUT2.spacing2}px;
  margin-bottom: ${LAYOUT2.spacing3}px;
`;

const LOGO_PREVIEW_STYLE = css`
  max-width: 200px;
  max-height: 100px;
  margin-top: ${LAYOUT2.spacing2}px;
  border: 1px solid var(--border);
  padding: ${LAYOUT2.spacing1}px;
  background: white;
`;

const FILE_INPUT_STYLE = css`
  margin-top: ${LAYOUT2.spacing2}px;
`;

const LOGO_ACTIONS_STYLE = css`
  display: flex;
  gap: ${LAYOUT2.spacing2}px;
  margin-top: ${LAYOUT2.spacing2}px;
`;

interface SettingsModalProps {
  visible: boolean;
  onClose: () => void;
}

const formatSettingKey = (key: string): string => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

export const SettingsModal = ({ visible, onClose }: SettingsModalProps) => {
  const { orgSettingsByKey, orgSettingsMutators, isLoading } = useOrgSettings();
  const { showToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const exportsSettings = orgSettingsByKey?.exports;
  const currentLogoSvg = exportsSettings?.logoSvg;

  const handleToggleSetting = async (settingsKey: string, dataKey: string, currentSetting: SettingValue) => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      await orgSettingsMutators.update(settingsKey, {
        data: {
          [dataKey]: {
            ...currentSetting,
            value: !currentSetting.value,
          },
        },
      });
      showToast('Setting updated successfully', 'success');
    } catch (error: any) {
      showToast('Error updating setting: ' + error.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'image/svg+xml') {
      showToast('Please select an SVG file', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const reader = new FileReader();
      reader.onload = async e => {
        const svgContent = e.target?.result as string;
        try {
          await orgSettingsMutators.update('exports', {
            logoSvg: svgContent,
          });
          showToast('Logo uploaded successfully', 'success');
        } catch (error: any) {
          showToast('Error uploading logo: ' + error.message, 'error');
        } finally {
          setIsSaving(false);
        }
      };
      reader.readAsText(file);
    } catch (error: any) {
      showToast('Error reading file: ' + error.message, 'error');
      setIsSaving(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      await orgSettingsMutators.update('exports', {
        logoSvg: null,
      });
      showToast('Logo removed successfully', 'success');
    } catch (error: any) {
      showToast('Error removing logo: ' + error.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!visible) return null;

  return (
    <div css={MODAL_OVERLAY_STYLE} onClick={onClose}>
      <div css={MODAL_CONTENT_STYLE} onClick={event => event.stopPropagation()}>
        <h2 css={MODAL_TITLE_STYLE}>Organization Settings</h2>

        {isLoading && (
          <div css={SETTINGS_SECTION_STYLE}>
            <div css={SETTING_LABEL_STYLE}>Loading settings...</div>
          </div>
        )}

        {!isLoading && orgSettingsByKey && Object.entries(orgSettingsByKey).map(([settingsKey, settings]) => {
          const settingsData = settings.data as SettingsData;

          return (
            <div key={settingsKey} css={SETTINGS_SECTION_STYLE}>
              <div css={SECTION_TITLE_STYLE}>{formatSettingKey(settingsKey)}</div>
              {Object.entries(settingsData).map(([dataKey, setting]) => {
                const settingValue = asSettingValue(setting);
                const { value, type = 'boolean', helpText, helpTitleText } = settingValue;

                return (
                  <div key={dataKey} css={SETTING_ROW_STYLE}>
                    <div css={SETTING_INFO_STYLE}>
                      <label css={SETTING_LABEL_STYLE}>
                        {helpTitleText || formatSettingKey(dataKey)}
                      </label>
                      {helpText && <div css={SETTING_HELP_TEXT_STYLE}>{helpText}</div>}
                    </div>
                    <div
                      css={[TOGGLE_SWITCH_STYLE, value && TOGGLE_SWITCH_CHECKED_STYLE]}
                      onClick={() => !isSaving && handleToggleSetting(settingsKey, dataKey, settingValue)}
                      style={{ opacity: isSaving ? 0.5 : 1, cursor: isSaving ? 'not-allowed' : 'pointer' }}
                    >
                      <div css={[TOGGLE_SLIDER_STYLE, value && TOGGLE_SLIDER_CHECKED_STYLE]} />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}

        {!isLoading && (
          <div css={LOGO_UPLOAD_SECTION_STYLE}>
            <div css={SECTION_TITLE_STYLE}>Upload SVG Logo</div>
            <div css={SETTING_HELP_TEXT_STYLE}>
              Upload an SVG logo to include in exported documents
            </div>

            <input
              type='file'
              accept='.svg,image/svg+xml'
              onChange={handleFileSelect}
              css={FILE_INPUT_STYLE}
              disabled={isSaving}
            />

            {currentLogoSvg && (
              <div css={LOGO_ACTIONS_STYLE}>
                <Button
                  onClick={handleRemoveLogo}
                  disabled={isSaving}
                  css={MODAL_BUTTON_STYLE}
                >
                  Remove Logo
                </Button>
              </div>
            )}
          </div>
        )}

        <div css={MODAL_BUTTONS_STYLE}>
          <Button onClick={onClose} css={MODAL_BUTTON_STYLE}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};
